import type { StaticOption } from '@blooq/flow'
import { IonIcon } from '@ionic/react'
import { chevronForwardOutline } from 'ionicons/icons'
import {
  useEffect,
  useRef,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react'

import { tap } from '../lib/native'
import { parseHex } from '../lib/theme'

import { OPTION_ICONS } from './steps/ChoiceDock'

/*
 * The sphere: a few hundred dots on the surface of a ball, in the business's
 * colour, turning slowly in the middle of the front door.
 *
 * It is the app's one moving thing and its one ornament. It replaced a solid
 * lit orb, and the reason is what the dots can DO that a gradient cannot: a
 * shell of points has an inside and an outside, so the menu can ride ON it —
 * each of the six actions is anchored to a spot on the surface and comes
 * round with the rotation, faded and small at the back, lit and large at the
 * front — and the same points can be pushed outward by a voice, so the
 * sphere visibly hears the customer rather than merely glowing while they
 * speak.
 *
 * Three ways to touch it. Drag anywhere and it turns under the finger, with
 * inertia, then drifts back to its own slow spin. Tap a satellite and that is
 * the menu choice. Tap the ball itself and it listens — the sphere IS the
 * microphone, the same rule the orb had.
 *
 * A canvas for the dots, real buttons for the satellites. The dots are pure
 * picture, redrawn sixty times a second; the labels are things a screen
 * reader must find and a finger must hit, so they are DOM elements whose
 * transforms are written from the same projection each frame. No React state
 * changes per frame: rotation, level and drag all live in refs.
 *
 * The same component, small and mute, is the "thinking" indicator in the
 * transcript and the drawer's empty-state picture — so the motif the customer
 * met on the front door is the one that answers them later.
 */

export type SphereMode = 'idle' | 'listening' | 'thinking'

interface Motion {
  /** Radians per second of the sphere's own turning. */
  spin: number
  /** How bright the dots and the bloom are. The voice adds on top. */
  glow: number
  /** Shimmer across the dots, 0–1. Thinking flickers; listening is steady. */
  shimmer: number
}

const MOTION: Record<SphereMode, Motion> = {
  idle: { spin: 0.2, glow: 0.82, shimmer: 0 },
  listening: { spin: 0.34, glow: 1, shimmer: 0 },
  thinking: { spin: 1.4, glow: 0.92, shimmer: 0.4 },
}

/** Looking slightly down onto the ball, so the top ring of satellites is seen. */
const TILT = -0.38
/** The satellites sit on two rings, above and below the equator, alternating. */
const SATELLITE_LATITUDE = 0.4
/** How far a finger has to travel before a press is a drag rather than a tap. */
const DRAG_SLOP = 7

/** A resting level for spheres that never listen. */
export const STILL: RefObject<number> = { current: 0 }

/** Points spread evenly over a unit sphere. */
function fibonacciSphere(count: number): Float32Array {
  const points = new Float32Array(count * 3)
  const golden = Math.PI * (3 - Math.sqrt(5))
  for (let index = 0; index < count; index += 1) {
    const y = count === 1 ? 0 : 1 - (index / (count - 1)) * 2
    const ring = Math.sqrt(1 - y * y)
    const theta = golden * index
    points[index * 3] = Math.cos(theta) * ring
    points[index * 3 + 1] = y
    points[index * 3 + 2] = Math.sin(theta) * ring
  }
  return points
}

/** Where each satellite is anchored, as a unit vector. */
function anchorOf(index: number, count: number): [number, number, number] {
  const longitude = (Math.PI * 2 * index) / Math.max(1, count) + Math.PI / 2
  const latitude = index % 2 === 0 ? SATELLITE_LATITUDE : -SATELLITE_LATITUDE
  return [
    Math.cos(latitude) * Math.cos(longitude),
    Math.sin(latitude),
    Math.cos(latitude) * Math.sin(longitude),
  ]
}

interface Drag {
  active: boolean
  moved: boolean
  startX: number
  startY: number
  lastX: number
  lastY: number
  lastT: number
}

export function DotSphere({
  accent,
  mode,
  level,
  options = [],
  onPick,
  onTap,
  tapLabel,
  disabled,
  points = 460,
  interactive = true,
}: {
  accent: string
  mode: SphereMode
  /** 0–1, written by whoever holds the microphone. Read every frame. */
  level: RefObject<number>
  /** The menu, riding on the surface. Empty for a mute sphere. */
  options?: StaticOption[]
  onPick?: (option: StaticOption) => void
  /** A tap on the ball itself. Absent, the ball is only for turning. */
  onTap?: () => void
  /** What a tap does, for the screen reader. */
  tapLabel?: string
  disabled?: boolean
  points?: number
  /** False for the small decorative spheres: no dragging, no tapping. */
  interactive?: boolean
}) {
  const box = useRef<HTMLDivElement>(null)
  const canvas = useRef<HTMLCanvasElement>(null)
  const satellites = useRef<(HTMLButtonElement | null)[]>([])

  // Read by the frame loop, so changing them never restarts it and snaps the
  // rotation back to zero.
  const current = useRef(mode)
  current.current = mode
  const drag = useRef<Drag>({
    active: false,
    moved: false,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    lastT: 0,
  })
  /** The rotation, and the finger's leftover velocity. */
  const turn = useRef({ yaw: 0, pitch: 0, yawVelocity: 0, pitchVelocity: 0 })

  const count = options.length

  useEffect(() => {
    const element = canvas.current
    const frame = box.current
    if (!element || !frame) return
    const context = element.getContext('2d')
    if (!context) return

    const rgb = parseHex(accent) ?? [3, 105, 161]
    const ink = (alpha: number) => `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    const cloud = fibonacciSphere(points)
    const order = new Uint16Array(points)
    const depth = new Float32Array(points)
    const anchors = Array.from({ length: count }, (_, index) => anchorOf(index, count))

    let width = 0
    let height = 0
    let handle = 0
    let last = performance.now()
    /** The smoothed swell. Follows the voice, and settles back without it. */
    let shown = 0

    const resize = () => {
      const ratio = window.devicePixelRatio || 1
      width = frame.clientWidth
      height = frame.clientHeight
      element.width = Math.round(width * ratio)
      element.height = Math.round(height * ratio)
      context.setTransform(ratio, 0, 0, ratio, 0, 0)
    }

    /** Rotate a unit vector by the current yaw and pitch. */
    const rotate = (x: number, y: number, z: number, yaw: number, pitch: number) => {
      const cosYaw = Math.cos(yaw)
      const sinYaw = Math.sin(yaw)
      const rx = x * cosYaw + z * sinYaw
      const rz = -x * sinYaw + z * cosYaw
      const cosPitch = Math.cos(pitch)
      const sinPitch = Math.sin(pitch)
      return [rx, y * cosPitch - rz * sinPitch, y * sinPitch + rz * cosPitch] as const
    }

    const draw = (now: number) => {
      const seconds = Math.min(0.05, (now - last) / 1000)
      last = now

      const motion = MOTION[current.current]
      const heard = current.current === 'listening' ? (level.current ?? 0) : 0
      // Quick to rise, slow to fall: a voice starts sharply and trails off.
      shown += (heard - shown) * (heard > shown ? 0.32 : 0.07)

      /*
       * Where the sphere is facing.
       *
       * Under a finger the rotation is whatever the finger last said. Let go
       * and the leftover velocity carries it on and decays, while its own
       * spin takes over underneath; the pitch eases back to level so a sphere
       * that was tipped over does not stay that way for the rest of the visit.
       */
      const state = turn.current
      if (!drag.current.active) {
        const decay = Math.pow(0.03, seconds)
        state.yawVelocity *= decay
        state.pitchVelocity *= decay
        state.yaw += (still ? 0 : motion.spin) * seconds + state.yawVelocity * seconds
        state.pitch += state.pitchVelocity * seconds
        state.pitch += (0 - state.pitch) * Math.min(1, seconds * 1.4)
      }
      const yaw = state.yaw
      const pitch = TILT + state.pitch

      context.clearRect(0, 0, width, height)

      const centreX = width / 2
      const centreY = height / 2
      // The ball takes most of its box; the satellites overhang the rest.
      const radius = Math.min(width, height) * (count > 0 ? 0.36 : 0.44)
      const breath = still ? 1 : 1 + 0.012 * Math.sin(now / 1500)
      const glow = Math.min(1.3, motion.glow * (1 + shown * 0.5))

      /*
       * The bloom under everything, so the ball sits IN the page rather than
       * on it. Brighter while listening: the light answers the voice too.
       */
      if (count > 0) {
        const reach = Math.min(radius * 1.9, Math.min(width, height) / 2)
        const bloom = context.createRadialGradient(centreX, centreY, radius * 0.2, centreX, centreY, reach)
        bloom.addColorStop(0, ink(0.2 * glow))
        bloom.addColorStop(0.55, ink(0.07 * glow))
        bloom.addColorStop(1, ink(0))
        context.fillStyle = bloom
        context.fillRect(0, 0, width, height)
      }

      // Project every dot, then paint back to front so the near ones sit on top.
      const projected = new Float32Array(points * 2)
      for (let index = 0; index < points; index += 1) {
        const [x, y, z] = rotate(
          cloud[index * 3]!,
          cloud[index * 3 + 1]!,
          cloud[index * 3 + 2]!,
          yaw,
          pitch,
        )
        /*
         * The voice, pushing the dots outward. Each dot on its own phase, so
         * the surface ripples rather than inflating like a balloon — a
         * balloon is a meter, a ripple is a thing that heard something.
         */
        const ripple = shown * 0.22 * (0.5 + 0.5 * Math.sin(index * 0.61 + now / 85))
        const reach = radius * breath * (1 + ripple)
        const perspective = 1 / (1 - z * 0.3)
        projected[index * 2] = centreX + x * reach * perspective
        projected[index * 2 + 1] = centreY - y * reach * perspective
        depth[index] = z
        order[index] = index
      }
      order.sort((a, b) => depth[a]! - depth[b]!)

      for (let step = 0; step < points; step += 1) {
        const index = order[step]!
        const near = (depth[index]! + 1) / 2
        const flicker =
          motion.shimmer > 0
            ? 1 - motion.shimmer * (0.5 + 0.5 * Math.sin(index * 0.37 + now / 110))
            : 1
        const alpha = Math.min(1, (0.14 + 0.72 * near ** 1.7) * glow * flicker)
        const size = (0.9 + 1.5 * near) * (1 + shown * 0.35)
        context.fillStyle = ink(alpha)
        context.beginPath()
        context.arc(projected[index * 2]!, projected[index * 2 + 1]!, size, 0, Math.PI * 2)
        context.fill()
      }

      /*
       * The satellites: each label placed where its anchor comes round to,
       * scaled and faded with depth, and marked on the surface with a larger
       * dot so it reads as pinned to the ball rather than floating past it.
       */
      for (let index = 0; index < anchors.length; index += 1) {
        const node = satellites.current[index]
        if (!node) continue
        const [ax, ay, az] = anchors[index]!
        const [x, y, z] = rotate(ax, ay, az, yaw, pitch)
        const perspective = 1 / (1 - z * 0.3)
        const reach = radius * breath * (1 + shown * 0.12)
        const screenX = centreX + x * reach * perspective
        const screenY = centreY - y * reach * perspective
        const near = (z + 1) / 2

        context.fillStyle = ink(Math.min(1, 0.35 + 0.65 * near) * glow)
        context.beginPath()
        context.arc(screenX, screenY, 2.6 + 2 * near, 0, Math.PI * 2)
        context.fill()

        const scale = 0.78 + 0.26 * near
        const opacity = z > 0 ? 1 : 0.18 + 0.82 * (1 + z)
        node.style.transform = `translate(-50%, -50%) translate(${screenX}px, ${screenY}px) scale(${scale})`
        node.style.opacity = opacity.toFixed(3)
        node.style.zIndex = String(Math.round(near * 10) + 1)
        // A label on the far side is a picture of what is coming, not a
        // target: a tap that lands on it goes to the ball instead.
        node.style.pointerEvents = z < -0.25 ? 'none' : ''
        node.tabIndex = z < -0.25 ? -1 : 0
      }
    }

    const loop = (now: number) => {
      draw(now)
      handle = requestAnimationFrame(loop)
    }

    resize()
    const observer = new ResizeObserver(() => {
      resize()
      draw(performance.now())
    })
    observer.observe(frame)

    /*
     * Reduced motion: the sphere does not turn on its own and nothing
     * ripples, but it still turns under a finger — a still picture that can be
     * looked around is not motion nobody asked for. The loop runs either way;
     * with `still` set, every term that moves by itself is zero.
     */
    handle = requestAnimationFrame(loop)

    return () => {
      cancelAnimationFrame(handle)
      observer.disconnect()
    }
  }, [accent, level, count, points])

  /*
   * Dragging.
   *
   * Handled on the window from the first press, so a finger that leaves the
   * box mid-turn keeps turning it. Nothing is captured: the satellites are
   * ordinary buttons and their clicks arrive as clicks — a press that MOVED
   * is remembered for one tick so that the click it ends with is ignored.
   */
  const onPointerDown = (event: ReactPointerEvent) => {
    if (!interactive || event.button !== 0) return
    const state = drag.current
    state.active = true
    state.moved = false
    state.startX = state.lastX = event.clientX
    state.startY = state.lastY = event.clientY
    state.lastT = performance.now()
    turn.current.yawVelocity = 0
    turn.current.pitchVelocity = 0

    const move = (pointer: PointerEvent) => {
      const now = performance.now()
      const dx = pointer.clientX - state.lastX
      const dy = pointer.clientY - state.lastY
      const dt = Math.max(1, now - state.lastT) / 1000
      state.lastX = pointer.clientX
      state.lastY = pointer.clientY
      state.lastT = now
      if (
        !state.moved &&
        Math.hypot(pointer.clientX - state.startX, pointer.clientY - state.startY) > DRAG_SLOP
      ) {
        state.moved = true
      }
      if (!state.moved) return
      const rotation = turn.current
      rotation.yaw += dx * 0.0085
      rotation.pitch = Math.max(-0.7, Math.min(0.7, rotation.pitch + dy * 0.006))
      // Smoothed, so a finger that stops before lifting hands over no fling.
      rotation.yawVelocity = rotation.yawVelocity * 0.5 + ((dx * 0.0085) / dt) * 0.5
      rotation.pitchVelocity = rotation.pitchVelocity * 0.5 + ((dy * 0.006) / dt) * 0.5
    }

    const up = () => {
      state.active = false
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      // The click this press ends with fires before this runs.
      window.setTimeout(() => {
        state.moved = false
      }, 0)
    }

    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
  }

  const onBallClick = (event: MouseEvent) => {
    if (!onTap || disabled || drag.current.moved) return
    if ((event.target as Element).closest('.blooq-sphere__sat')) return
    onTap()
  }

  const tappable = Boolean(onTap) && interactive

  return (
    <div
      ref={box}
      className={`blooq-sphere${interactive ? ' blooq-sphere--live' : ''}`}
      onPointerDown={onPointerDown}
    >
      <canvas ref={canvas} className="blooq-sphere__face" aria-hidden="true" />
      {/*
        The ball's own tap target: a round, invisible button the size of the
        ball, UNDER the satellites in the stack rather than around them — a
        button cannot contain buttons, and a screen reader reading "Speak to
        us" and then four choices inside it would be right to be confused.
      */}
      {tappable ? (
        <button
          type="button"
          className="blooq-sphere__ball"
          aria-label={tapLabel}
          disabled={disabled}
          onClick={onBallClick}
        />
      ) : null}
      {options.map((option, index) => (
        <button
          key={option.value}
          ref={(node) => {
            satellites.current[index] = node
          }}
          type="button"
          className="blooq-sphere__sat"
          disabled={disabled}
          onClick={() => {
            if (drag.current.moved || !onPick) return
            tap()
            onPick(option)
          }}
        >
          <IonIcon icon={OPTION_ICONS[option.value] ?? chevronForwardOutline} aria-hidden="true" />
          {option.label}
        </button>
      ))}
    </div>
  )
}
